import React, { memo, useState } from 'react'
import classNames from 'classnames'
import { TransformComponent, TransformWrapper, useControls, useTransformEffect } from 'react-zoom-pan-pinch'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { MinusIcon } from '@/atoms/Icons/MinusIcon'
import { PlusIcon } from '@/atoms/Icons/PlusIcon'
import { Image } from '@/atoms/Image'
import { CaptionMD } from '@/atoms/Text'
import { Tooltip } from '@/molecules/Tooltip'
import { ReservationSeat } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import useMeasure from '@/utils/useMeasure'
import {
  AvailableSeatType,
  findAvailableSeatType,
} from '@/views/TicketCheckoutViews/SeatMapView/components/useAvailableSeatTypes'
import { ShowtimeReservation } from '../SeatMapView'
import { Seat, SEAT_WIDTH } from './Seat'
import SeatLegendButton from './SeatLegendButton'
import { ZoomResetterOnHeightChange } from './ZoomResetterOnHeightChange'

const Controls = ({
  onOpenSeatLegend,
  setScale,
  friendSeat,
  isViewOnly,
  isMultiOrderExperiment = false,
  onToggleSelectAllSeats,
  allEligibleSeatsSelected = false,
  isSelectAllSeatsPopoverVisible = false,
  isPolishExperiment = false,
}: {
  onOpenSeatLegend: () => void
  setScale: React.Dispatch<React.SetStateAction<number>>
  friendSeat?: AvailableSeatType
  isViewOnly?: boolean
  isMultiOrderExperiment?: boolean
  onToggleSelectAllSeats?: () => void
  allEligibleSeatsSelected?: boolean
  isSelectAllSeatsPopoverVisible?: boolean
  isPolishExperiment?: boolean
}) => {
  const { zoomIn, zoomOut } = useControls()
  const { t } = useTranslate('tickets')
  useTransformEffect((ref) => {
    const scale = ref?.state?.scale
    setScale(scale)
  })

  if (isViewOnly) return null

  const showSelectAllButton = isMultiOrderExperiment && !!onToggleSelectAllSeats

  return (
    <FlexColumn>
      <FlexRow
        className={classNames('justify-between bg-core-gray-950', isPolishExperiment ? 'lg:hidden' : 'md:hidden')}
      >
        <FlexRow className="gap-3">
          {showSelectAllButton && (
            <Tooltip
              content={t(
                'selectAllSeatsAvailableTooltip',
                'The "Select All Seats" feature is now available for this showtime.',
              )}
              forceVisible={isSelectAllSeatsPopoverVisible}
              preferredPosition="top"
              className="border border-core-gray-200 !bg-white !text-core-gray-950 shadow-lg after:absolute after:bottom-0 after:left-4 after:h-2 after:w-2 after:translate-y-1/2 after:rotate-45 after:border after:border-core-gray-200 after:bg-white"
            >
              <SeatLegendButton
                className={isSelectAllSeatsPopoverVisible ? 'animate-pulse border border-white' : undefined}
                onClick={onToggleSelectAllSeats}
                label={
                  allEligibleSeatsSelected
                    ? t('deselectAllSeats', 'Deselect All Seats')
                    : t('selectAllSeats', 'Select All Seats')
                }
                showIcon={false}
              />
            </Tooltip>
          )}
          <SeatLegendButton
            onClick={onOpenSeatLegend}
            label={isMultiOrderExperiment ? t('legend', 'Legend') : t('seatLegend', 'Seat Legend')}
            showIcon={!isMultiOrderExperiment}
          />
        </FlexRow>
        <FlexRow className="gap-3">
          <Button
            onClick={() => zoomOut()}
            className="flex items-center gap-2 bg-core-gray-900 px-3 py-2"
            variant="core-gray-800"
          >
            <MinusIcon size={12} />
          </Button>
          <Button
            onClick={() => zoomIn()}
            className="flex items-center gap-2 bg-core-gray-900 px-3 py-2"
            variant="core-gray-800"
          >
            <PlusIcon size={12} />
          </Button>
        </FlexRow>
      </FlexRow>
      {friendSeat && (
        <div className="mx-auto mt-2 flex items-center gap-2">
          <Image alt="seat icon" src={friendSeat.cloudinaryPath} width={20} height={20} />
          <CaptionMD className="text-left" weight="semibold">
            {t('friendSeat', 'Friend Seat')}
          </CaptionMD>
        </div>
      )}
    </FlexColumn>
  )
}

export interface SeatMapLayoutProps {
  rows: number
  columns: number
  seats: NonNullable<NonNullable<ShowtimeReservation>['showtimeSeatingInfo']>['seats']
  onSeatPressed: (seat: ReservationSeat) => void
  selectedSeats: Map<string, ReservationSeat>
  onOpenSeatLegend: () => void
  availableSeatTypes: AvailableSeatType[]
  isViewOnly?: boolean
  useZoomResetter?: boolean
  isMultiOrderExperiment?: boolean
  onToggleSelectAllSeats?: () => void
  allEligibleSeatsSelected?: boolean
  isSelectAllSeatsPopoverVisible?: boolean
  isPolishExperiment?: boolean
}
const SeatMapLayoutInternal = ({
  seats,
  rows,
  columns,
  onSeatPressed,
  selectedSeats,
  onOpenSeatLegend,
  availableSeatTypes,
  isViewOnly = false,
  useZoomResetter = false,
  isMultiOrderExperiment = false,
  onToggleSelectAllSeats,
  allEligibleSeatsSelected = false,
  isSelectAllSeatsPopoverVisible = false,
  isPolishExperiment = false,
}: SeatMapLayoutProps) => {
  const { t } = useTranslate('tickets')
  const [scale, setScale] = useState(1)
  const seatHeight = 50
  const horizontalSpacing = 0
  const verticalSpace = 10
  const SCREEN_TO_SEAT_PADDING = Math.floor((seatHeight * 2) / 3)
  const [ref, dimensions] = useMeasure<SVGSVGElement>()

  if (!seats || seats?.length === 0) return null
  const SCREEN_START_X = Math.floor(SEAT_WIDTH / 2)
  const SEAT_START_X = SCREEN_START_X + Math.floor(horizontalSpacing / 2)
  const SCREEN_START_Y = Math.floor(seatHeight * 1.5)
  const SEAT_START_Y = SCREEN_START_Y + Math.floor(verticalSpace / 2)
  const theaterWidth = Math.floor(SCREEN_START_X * 2 + columns * (SEAT_WIDTH + horizontalSpacing))

  const theaterHeight = Math.floor(SCREEN_START_Y * 2 + rows * (seatHeight + verticalSpace) + SCREEN_TO_SEAT_PADDING)

  const theaterThird = Math.floor(theaterWidth / 3)
  const screenStart = `${SCREEN_START_X},${SCREEN_START_Y}`
  const screenEnd = `${theaterWidth - SCREEN_START_X},${SCREEN_START_Y}`
  const screenPathArc = `M ${screenStart} C ${theaterThird},15 ${theaterThird * 2},15 ${screenEnd}`

  const friendSeat = findAvailableSeatType(availableSeatTypes, 'FRIEND_SEAT')

  return (
    <TransformWrapper
      panning={{ disabled: scale === 1, velocityDisabled: true }}
      initialScale={1}
      initialPositionX={0}
      initialPositionY={0}
      doubleClick={{ disabled: isPolishExperiment }}
    >
      <TransformComponent contentClass="!w-full !h-full" wrapperClass="!w-full !h-full">
        <svg ref={ref} id="SeatMap" width="100%" height="100%" viewBox={`0 0 ${theaterWidth} ${theaterHeight}`}>
          <path
            d={screenPathArc}
            fill="none"
            stroke="#C3C1C0"
            strokeWidth={Math.floor(seatHeight / 4)}
            strokeLinecap="round"
          />
          <path
            fill="none"
            d={screenPathArc}
            stroke="#3C3C3C"
            width="30%"
            strokeWidth={Math.floor(seatHeight / 2.5)}
            strokeLinecap="round"
            transform={`translate(0, -5)`}
          />
          <text
            dominantBaseline="middle"
            textAnchor="middle"
            fontWeight="bold"
            className="translate-y-[4rem] text-2xl text-white md:translate-y-[1.45rem] md:text-[0.75rem]"
            x="50%"
            fill="currentColor"
          >
            {t('screen', 'SCREEN')}
          </text>
          <g transform={`translate(${SEAT_START_X}, ${SEAT_START_Y + SCREEN_TO_SEAT_PADDING})`}>
            {seats?.map((seat) => (
              <Seat
                key={seat?.seatId}
                seat={seat as ReservationSeat}
                onSeatPressed={onSeatPressed}
                selected={selectedSeats.has(seat?.seatId as string)}
              />
            ))}
          </g>
        </svg>
      </TransformComponent>
      <Controls
        friendSeat={friendSeat}
        setScale={setScale}
        onOpenSeatLegend={onOpenSeatLegend}
        isViewOnly={isViewOnly}
        isMultiOrderExperiment={isMultiOrderExperiment}
        onToggleSelectAllSeats={onToggleSelectAllSeats}
        allEligibleSeatsSelected={allEligibleSeatsSelected}
        isSelectAllSeatsPopoverVisible={isSelectAllSeatsPopoverVisible}
        isPolishExperiment={isPolishExperiment}
      />
      {useZoomResetter && <ZoomResetterOnHeightChange height={dimensions?.height} />}
    </TransformWrapper>
  )
}

export const SeatMapLayout = memo(SeatMapLayoutInternal)
